import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Table, Spin } from 'antd';
import { getTenantsTableColumns } from './utils/getTenantsTableColumns';
import useTablePagination from '../../../../hooks/useTablePagination';
import useTableChange from '../../../../hooks/useTableChange';
import { sortableColumnsData } from '../constants/sortableColumnsData';
import { filterableColumnsData } from '../constants/filterableColumnsData';
import AddTenantModal from '../../modals/AddTenantModal/AddTenantModal';
import useChangeModalVisible from '../../../../../../hooks/useChangeModalVisible';
import { getReplacedString } from '../../../../../../helpers/getReplacedString';
import { tenantsTableComponents } from './constants/tenantsTableComponents';
import { modes } from '../../MainHeader/components/Search';
import useHouseFilterOptions from './hooks/useHouseFilterOptions';
import useDeleteTenantComfirm from '../../../../hooks/useDeleteTenantComfirm';

const filterCb = ({ searchVal }) => {
  const search = getReplacedString(searchVal);

  return (tenant) => {
    const name = getReplacedString(tenant.name);
    const phone = getReplacedString(tenant.phone);
    const email = getReplacedString(tenant.email);
    const fio = getReplacedString(tenant.fio);

    return (
      name.includes(search) ||
      phone.includes(search) ||
      email.includes(search) ||
      fio.includes(search)
    );
  };
};

const TenantsTable = ({
  onRowClick,
  tenants = [],
  pagination = true,
  companyName,
  removePadding = false,
  queryKeys,
  ...props
}) => {
  const [tenant, setTenant] = useState(null);

  const searchFieldValue = useSelector(
    (state) => state.housingStockStore.searchFieldValue[modes.tenants]
  );

  const { handleDeleteTenant } = useDeleteTenantComfirm();

  const [isFiltering, setIsFiltering] = useState(false);
  const [filteredBySearchTenants, setFilteredBySearchTenants] = useState(() =>
    searchFieldValue
      ? tenants.filter(filterCb({ searchVal: searchFieldValue }))
      : tenants
  );

  const params = useParams();

  const {
    visible: addTenantModalVisible,
    showModal: showAddTenantModal,
    hideModal: hideAddTenantModal,
  } = useChangeModalVisible();

  const {
    data: filteredAndSortedTenants,
    sortedInfo,
    filteredInfo,
    handleTableChange,
  } = useTableChange({
    initialData: filteredBySearchTenants,
    sortableColumnKeys: sortableColumnsData,
    filterableColumnKeys: filterableColumnsData,
    depsToReset: [params.companyId],
  });

  const { shownItems: shownTenants, pagination: paginationBlock } =
    useTablePagination(filteredAndSortedTenants);

  const houseOptions = useHouseFilterOptions(filteredBySearchTenants);

  const handleEditBtnClick = (tenant) => {
    setTenant(tenant);
    showAddTenantModal();
  };

  useEffect(() => {
    setTenant((prev) =>
      !prev ? prev : tenants.find((t) => t.id === prev.id) || null
    );
  }, [tenants]);

  const handleDeleteBtnClick = (tenant) => {
    if (!tenant?.bindId) {
      return;
    }

    handleDeleteTenant({
      tenantId: tenant?.id,
      tenantBindId: tenant?.bindId,
      addressId: tenant?.addressId,
      address: tenant?.address,
      phone: tenant?.phone,
      onOk: () => {
        handleCancel();
      },
    });
  };

  const handleCancel = () => {
    setTenant(null);
    hideAddTenantModal();
  };

  useEffect(() => {
    let timeoutId;

    if (searchFieldValue) {
      setIsFiltering(true);
      timeoutId = setTimeout(() => {
        const newData = tenants.filter(
          filterCb({ searchVal: searchFieldValue })
        );
        setFilteredBySearchTenants(newData);
        setIsFiltering(false);
      }, 1000);
    } else {
      setIsFiltering(false);
      setFilteredBySearchTenants(tenants);
    }

    return () => clearTimeout(timeoutId);
  }, [tenants, searchFieldValue]);

  return (
    <Spin spinning={isFiltering}>
      <Table
        {...props}
        dataSource={pagination ? shownTenants : filteredAndSortedTenants}
        components={tenantsTableComponents}
        rowKey="key"
        pagination={false}
        columns={getTenantsTableColumns({
          companyName,
          sortedInfo,
          filteredInfo,
          handlers: {
            onEditBtnClick: handleEditBtnClick,
            onDeleteBtnClick: handleDeleteBtnClick,
          },
          houseOptions,
        })}
        onChange={handleTableChange}
      />
      {pagination && paginationBlock}
      <AddTenantModal
        visible={addTenantModalVisible}
        onCancel={handleCancel}
        address={tenant?.address}
        addressId={tenant?.addressId}
        initialValues={tenant}
        queryKeys={queryKeys}
        withDeleteBtn={false}
      />
    </Spin>
  );
};

export default TenantsTable;
